<script setup lang="ts">
// Card do bônus resgatável só pelo app (ENG-799), nas duas variantes:
//
//   `download`  — jogador FORA do app. Convida a baixar; o CTA abre o modal
//                 "Baixe o APP e resgate o seu bônus" e o resgate fica
//                 travado.
//   `exclusive` — jogador DENTRO do app. Mesma arte, mensagem trocada: ele já
//                 tem o app, então nada de convite nem de fita de loja — o CTA
//                 resgata de verdade.
// Figma: node 23956:298146 do arquivo "1. Página Principal - Aposta Tudo".
//
// Substitui o card comum por inteiro — não é um estado dele. Por isso vive
// num componente próprio: o `RewardCard.vue` só decide qual dos dois montar.
//
// As medidas do Figma vieram de um frame redimensionado à mão (0.771px,
// 4.625px, 9.251px...). O fator do botão é 77,1% exato — `9.251 / 0.771 = 12`
// —, então os valores do arquivo são proporção, não spec. Aqui eles estão
// reproduzidos como no arquivo, mas multiplicados por uma unidade `--u` em
// `cqw`: `--u: 0.277008cqw` = 100/361, então --u = largura_do_card / 361. Na
// largura de referência (361px, ~a do card no celular) --u = 1px e o card sai
// idêntico ao arquivo; no desktop, onde o painel dá ~550px, tudo cresce junto
// na mesma proporção em vez de ficar com texto de 7px num card largo.
//
// Variável e não `em`: `em` COMPÕE — assim que um elemento recebe
// `font-size: 7em`, o `line-height: 13em` dele passa a valer 13x o próprio
// tamanho, não a base, e o card estourava pra 2,5x a altura.
import { parseRewardDescription } from '@/utils/rewards';

type Props = {
  reward: Reward;
  variant: 'download' | 'exclusive';
};

const { reward, variant } = defineProps<Props>();

const emit = defineEmits<{ redeem: [] }>();

const isDownload = computed(() => variant === 'download');

// MESMO sinal do card comum (RewardCard.vue): no estreito, prazo e ação saem
// da linha de metadados e viram um par pastilha+botão embaixo. Não dá pra
// fazer isso por consulta de container em CSS porque WidgetsCountdown roda um
// setInterval — as duas versões não podem ficar montadas ao mesmo tempo.
const { isLayoutMobileOrTablet: isMobile } = useLayout();

const { showMoneyFormat } = useWallet();
const { normalizeThumbnail, getThumbnailPathSet } = useCasino();

// Cartaz próprio do bônus app-only (`components/app-download/BonusModal.vue`),
// não o genérico "Baixe o aplicativo" do menu lateral (ENG-495).
const appDownload = useAppDownloadBonusStore();

function acionar() {
  if (isDownload.value) {
    appDownload.open();

    return;
  }

  emit('redeem');
}

// mesmas regras de texto do card comum: o valor sai de dentro das chaves do
// `player_desc` e, na falta dele, do `amount`.
const parsed = computed(() => parseRewardDescription(reward.player_desc));

const value = computed(() => {
  if (parsed.value.value) {
    return parsed.value.value;
  }

  return reward.amount ? showMoneyFormat({ value: reward.amount / 100 }) : '';
});

const countDownTotalTime = computed(() => {
  const diff = new Date(reward.expires_at).getTime() - Date.now();

  return diff > 0 ? diff / 1000 : 0;
});

const isCountdownFinished = ref(false);

// miniatura do jogo pelas MESMAS regras do card comum (RewardCard.vue). Sem
// `game_image` — prêmio que não é de um jogo específico — cai na ilustração 3D
// do Figma.
const thumbnailPath = computed(() =>
  reward.game_image ? `${normalizeThumbnail(reward.game_image)}/public` : '',
);

const thumbnailPathSet = computed(() =>
  reward.game_image ? getThumbnailPathSet(reward.game_image) : '',
);
</script>

<template>
  <!-- A barra azul é `border-left` DE VERDADE, como nos cards normais: ela
       acompanha a curva do canto arredondado e faz a pontinha característica.
       Um retângulo posicionado no lugar dela fica com a ponta reta, cortada
       pelo `overflow-hidden`, e destoa do resto da lista.
       A largura é 5px FIXOS, não escalada: os cards vizinhos usam 5px em
       qualquer largura, e a barra tem que casar com a deles. (De quebra, o
       browser não resolve `cqw` em `border-width` — a versão escalada caía
       silenciosamente pro 1px da classe.) O `pl` desconta a borda porque no
       `border-box` ela empurra o conteúdo. No estreito o recuo lateral é o
       MESMO do card comum (`px-[16px]` + a borda de 5px = 21px à esquerda,
       16px à direita) — ele aparece na mesma lista, e o desalinhamento com os
       vizinhos salta aos olhos. No largo segue a proporção do arquivo.
       O `padding-top` é a ÚNICA medida que não escala junto: no Figma são 15px
       a 361px de largura, e escalando linearmente viravam ~24px no desktop —
       espaço vazio infla rápido, e o card ficava com um buraco entre o topo e a
       ilustração. Travado no teto de 15px: abaixo de 361px ainda encolhe junto,
       acima disso para de crescer. Vai no `style` porque `min()` com `calc()`
       dentro de classe arbitrária depende do JIT gerar a regra. -->
  <div
    class="w-full"
    style="container-type: inline-size"
  >
    <div
      class="border-solid relative flex w-full flex-col gap-[calc(var(--u)*5)] overflow-hidden rounded-[calc(var(--u)*8)] border-l-[5px] border-v2-primary bg-[#000d1d] pb-[calc(var(--u)*6)]"
      :class="[
        isMobile
          ? 'pl-[16px] pr-[16px]'
          : 'pl-[calc(var(--u)*15_-_5px)] pr-[calc(var(--u)*15)]',
        // No estreito o conteúdo começa ABAIXO da fita. Ela é `absolute` no
        // topo direito e, com o card curto, caía em cima da primeira linha de
        // texto — no card de download o título passava 107px por baixo dela.
        // Empurrar é melhor que reservar espaço à direita: a 305px sobrariam
        // ~80px para o texto, e 'Baixe o app e ganhe' viraria 'Baixe o a…'.
        // A de download é mais alta (dois textos + ícones das lojas), por isso
        // dois valores.
        isMobile ? (isDownload ? 'pt-[27px]' : 'pt-[23px]') : '',
      ]"
      :style="
        isMobile
          ? '--u: 0.277008cqw'
          : '--u: 0.277008cqw; padding-top: min(calc(var(--u) * 15), 15px)'
      "
    >
      <!-- O fundo é um SVG esticado, não `linear-gradient` do CSS. Um gradiente
           de ângulo fixo se redistribui quando a proporção da caixa muda, e o
           card é fluido: o desenho tem 361x128 (2.8:1), mas no desktop ele fica
           5:1 e as cores saíam diferentes do arquivo. Com `preserveAspectRatio
           ="none"` o gradiente estica junto, como o glow. -->
      <img
        alt=""
        aria-hidden="true"
        class="pointer-events-none absolute inset-0 h-full w-full"
        :src="
          getAssetsUrlForCDN('/assets/images/user-v2/rewards/app-only-bg.svg')
        "
      />

      <!-- Fita "Download grátis em IOS e Android" (Figma 23956:298171).
           A forma e o conteúdo vivem no MESMO elemento, com a fita esticando
           (`preserveAspectRatio="none"`) atrás do texto: separados, o bloco de
           texto crescia mais que a forma e vazava. O SVG já vem na horizontal
           (`-h`), com o entalhe à esquerda, pra não depender de rotação — uma
           caixa girada 90° troca largura por altura e não estica junto.
           Ancorada pela DIREITA porque o card é fluido: no Figma ela sangra
           3.536px pra fora e o `overflow-hidden` corta.
           O `pl` no estreito é 15px e não menos: o entalhe da ponta ocupa ~9,6%
           da largura da fita (8.1 das 84.7 unidades do SVG, que estica junto),
           o que dá ~10px numa fita de 105px. Abaixo disso o texto começa DENTRO
           da ponta e encosta nela. O download precisa de 2px a mais porque a
           fita dele é mais larga (dois textos + os ícones das lojas), e ponta
           maior come mais espaço. -->
      <div
        class="pointer-events-none absolute z-10 flex items-center whitespace-nowrap"
        :class="
          isMobile
            ? [
                'right-[-3px] top-[4px] gap-[4px] pr-[6px]',
                isDownload ? 'h-[19px] pl-[17px]' : 'h-[15px] pl-[15px]',
              ]
            : 'right-[calc(var(--u)*-3.536)] top-[calc(var(--u)*4.69)] h-[calc(var(--u)*19.039)] gap-[calc(var(--u)*5.018)] pl-[calc(var(--u)*13)] pr-[calc(var(--u)*8.5)]'
        "
      >
        <img
          alt=""
          aria-hidden="true"
          class="absolute inset-0 h-full w-full"
          :src="
            getAssetsUrlForCDN(
              '/assets/images/user-v2/rewards/app-only-ribbon-h.svg',
            )
          "
        />

        <!-- Fora do app: ícones das lojas (do Figma, #454545 — os de
             `app-download/` são brancos e sumiriam na fita branca).
             Dentro do app: nada de loja, o jogador já baixou. -->
        <img
          v-if="isDownload"
          alt=""
          aria-hidden="true"
          class="relative shrink-0"
          :class="
            isMobile
              ? 'h-[10px] w-[9px]'
              : 'h-[calc(var(--u)*11.085)] w-[calc(var(--u)*9.422)]'
          "
          :src="
            getAssetsUrlForCDN(
              '/assets/images/user-v2/rewards/app-only-apple.svg',
            )
          "
        />

        <img
          v-if="isDownload"
          alt=""
          aria-hidden="true"
          class="relative shrink-0"
          :class="
            isMobile
              ? 'h-[10px] w-[9px]'
              : 'h-[calc(var(--u)*10.871)] w-[calc(var(--u)*9.42)]'
          "
          :src="
            getAssetsUrlForCDN(
              '/assets/images/user-v2/rewards/app-only-play.svg',
            )
          "
        />

        <span class="relative flex shrink-0 flex-col text-[#454545]">
          <template v-if="isDownload">
            <span
              class="font-normal"
              :class="
                isMobile
                  ? 'text-[8px] leading-[8px]'
                  : 'text-[calc(var(--u)*4.6)] leading-[calc(var(--u)*6)]'
              "
            >
              Download grátis em
            </span>
            <span
              class="font-bold"
              :class="
                isMobile
                  ? 'text-[8px] leading-[9px]'
                  : 'text-[calc(var(--u)*5.615)] leading-[calc(var(--u)*8.422)]'
              "
            >
              IOS e Android
            </span>
          </template>

          <span
            v-else
            class="font-extrabold uppercase"
            :class="
              isMobile
                ? 'text-[8px] leading-[10px] tracking-[0.3px]'
                : 'text-[calc(var(--u)*6.5)] leading-[calc(var(--u)*9)] tracking-[calc(var(--u)*0.3)]'
            "
          >
            Exclusivo do app
          </span>
        </span>
      </div>

      <div
        class="relative flex items-center"
        :class="
          isMobile
            ? 'min-h-[48px] gap-[12px]'
            : 'min-h-[calc(var(--u)*58)] gap-[calc(var(--u)*17)]'
        "
      >
        <!-- brilho azul difuso (Figma 23956:298149). É filho da LINHA, não do
             card, e o `inset` negativo do arquivo faz a imagem render no tamanho
             natural do SVG. Em % da linha e não em px: o card é fluido e, num
             card largo, um glow de largura fixa deixa o rastro branco parado no
             meio. O SVG tem `preserveAspectRatio="none"`, então estica. Na
             largura de referência (361px) os % dão exatamente -85.91px/461.664px.
             Largura em % e altura FIXA: medi as
             duas alternativas e escalar a altura junto joga o rastro branco
             inferior pra fora do card no desktop. O y é -63 e não os -55.57 do
             arquivo porque a linha comprimiu de 65 pra 58px no render do Figma e
             o glow, ancorado nela, andou junto.
             No estreito o brilho perde intensidade, e NÃO altura. Encurtar
             foi a primeira tentativa e piorou: o desenho é um degradê, então
             espremer a caixa concentra o branco mais pra cima e ele cai em
             cima da pastilha de prazo (contraste do contador foi de 15:1 pra
             1,3:1). Baixar a opacidade mantém o blob claro onde ele estava,
             embaixo e fora do card. Tapar com um véu escuro, que foi a
             tentativa anterior, escondia o brilho inteiro. -->
        <img
          alt=""
          aria-hidden="true"
          class="pointer-events-none absolute left-[-25.95%] top-[calc(var(--u)*-63)] h-[calc(var(--u)*215.641)] w-[139.5%] max-w-none"
          :class="isMobile ? 'opacity-[0.55]' : ''"
          :src="
            getAssetsUrlForCDN(
              '/assets/images/user-v2/rewards/app-only-glow.svg',
            )
          "
        />

        <img
          :alt="parsed.description"
          class="relative shrink-0"
          :class="[
            isMobile
              ? 'size-[48px]'
              : 'top-[calc(var(--u)*-2)] size-[calc(var(--u)*58)]',
            thumbnailPath
              ? [
                  'object-cover',
                  isMobile ? 'rounded-[11px]' : 'rounded-[calc(var(--u)*11)]',
                ]
              : 'object-contain',
          ]"
          :src="
            thumbnailPath ||
            getAssetsUrlForCDN(
              '/assets/images/user-v2/rewards/app-only-illustration.webp',
            )
          "
          :srcset="thumbnailPathSet || undefined"
        />

        <!-- Em fluxo com margens, não posicionados: as margens reproduzem as
             mesmas posições do Figma (título em y0, valor em y16, metadados em
             y32 da coluna). A folga da linha é 8.2px no celular e a
             do arquivo (9.941) de `sm` pra cima: o Chrome desenha esse texto ~4%
             mais largo que o Figma e faltavam 5px pra caber em 358px, que é a
             largura real do card no celular. O `flex-wrap` fica de rede.
             No estreito a tipografia é fixa e um pouco maior que a do
             arquivo — só o suficiente pra sair de baixo do piso de fonte que
             as engines móveis aplicam (o original dava ~7px, que elas
             inflavam sozinhas, e o layout saía imprevisível). NÃO é a escala
             do card comum onde faz sentido: descrição 14px e metadados 11px,
             os mesmos dele. Coube porque a variante do app não tem mais a
             chamada — era ela que fazia o card crescer com essa escala.
             Miniatura e folga também são as do vizinho (48px e 12px): com a
             tipografia igual, manter a imagem maior era o que sobrava de
             desproporção entre os dois cards na mesma lista.
             `min-h` e NÃO `h`: com altura fixa, quando a linha de metadados
             quebrava o conteúdo passava dos 59u, os filhos encolhiam (flex
             encolhe por padrão) e a descrição — que tem `truncate`, ou seja
             `overflow: hidden` — era espremida a zero e SUMIA. O título não
             sumia junto só porque não recorta o que transborda. -->
        <div
          class="relative flex min-w-0 flex-1 flex-col justify-center"
          :class="
            isMobile
              ? 'min-h-[48px] gap-[3px]'
              : 'min-h-[calc(var(--u)*59)] gap-[calc(var(--u)*5)]'
          "
        >
          <!-- Só na variante de download. Dentro do app a fita já diz
               "EXCLUSIVO DO APP", e repetir isso numa chamada roubava a linha
               principal da descrição do prêmio. -->
          <p
            v-if="isDownload"
            class="font-medium text-white"
            :class="
              isMobile
                ? 'text-[14px] leading-[18px]'
                : 'text-[calc(var(--u)*12)] leading-[calc(var(--u)*13)]'
            "
          >
            Baixe o app e ganhe
          </p>

          <!-- O Figma só tem a chamada fixa, mas aí o card não diz QUAL é o
               prêmio: num "Rodadas grátis no jogo Fortune Ox" o jogador perde
               o jogo. Entra a mesma descrição do card comum. -->
          <p
            class="truncate"
            :class="
              isMobile
                ? 'text-[14px] font-semibold leading-[19px] text-white'
                : 'text-[calc(var(--u)*8.5)] font-medium leading-[calc(var(--u)*10)] text-white/70'
            "
          >
            {{ parsed.description }}
          </p>

          <!-- Regra do doc "Prêmios e Recompensas: tratamento do valor entre
               chaves": quantidade e valor só aparecem quando existem — nada de
               inventar "Quantidade: 1" nem deixar um "Valor:" vazio. Os
               separadores acompanham, senão sobra barrinha solta.
               Ordem valor → quantidade, a mesma dos outros cards. -->
          <div
            class="flex flex-wrap items-baseline"
            :class="
              isMobile
                ? 'gap-x-[9px] gap-y-[2px]'
                : 'gap-x-[calc(var(--u)*8.2)] gap-y-[calc(var(--u)*2)]'
            "
          >
            <span
              v-if="value"
              class="whitespace-nowrap"
              :class="
                isMobile
                  ? 'text-[11px] leading-[15px] text-white/50'
                  : 'text-[calc(var(--u)*7.332)] font-semibold leading-[calc(var(--u)*7.332)] text-white'
              "
            >
              Valor: {{ value }}
            </span>

            <span
              v-if="value && reward.quantity"
              class="shrink-0 self-center bg-white/40"
              :class="
                isMobile
                  ? 'h-[13px] w-px'
                  : 'h-[calc(var(--u)*13.065)] w-[calc(var(--u)*1)]'
              "
            />

            <span
              v-if="reward.quantity"
              class="whitespace-nowrap"
              :class="
                isMobile
                  ? 'text-[11px] leading-[15px] text-white/50'
                  : 'text-[calc(var(--u)*7.332)] font-semibold leading-[calc(var(--u)*7.332)] text-white'
              "
            >
              Quantidade: {{ reward.quantity }}
            </span>

            <span
              v-if="!isMobile && (reward.quantity || value)"
              class="shrink-0 self-center bg-white/40"
              :class="
                isMobile
                  ? 'h-[13px] w-px'
                  : 'h-[calc(var(--u)*13.065)] w-[calc(var(--u)*1)]'
              "
            />

            <span
              v-if="!isMobile"
              class="flex items-baseline gap-[calc(var(--u)*3.532)] whitespace-nowrap font-semibold"
            >
              <span
                class="text-[calc(var(--u)*7.064)] leading-[calc(var(--u)*11.302)] text-white/50"
              >
                Expira em:
              </span>

              <WidgetsCountdown
                v-if="countDownTotalTime && !isCountdownFinished"
                :total-time="countDownTotalTime"
                @timer-finished="isCountdownFinished = true"
              >
                <template #process="{ time }">
                  <span
                    class="text-[calc(var(--u)*9.183)] leading-[calc(var(--u)*9.183)] tabular-nums text-white"
                  >
                    {{ `${time.hours}h:${time.minutes}m:${time.seconds}s` }}
                  </span>
                </template>
                <template #finish>
                  <span
                    class="text-[calc(var(--u)*9.183)] leading-[calc(var(--u)*9.183)] text-white"
                  >
                    {{ $t('rewards.expired') }}
                  </span>
                </template>
              </WidgetsCountdown>

              <span
                v-else
                class="text-[calc(var(--u)*9.183)] leading-[calc(var(--u)*9.183)] text-white"
              >
                {{ $t('rewards.expired') }}
              </span>
            </span>
          </div>
        </div>
      </div>

      <!-- No ESTREITO o card se comporta como os vizinhos: prazo numa pastilha
           e ação ao lado, os dois em medidas FIXAS. Fixas de propósito — a
           escala por `cqw` levava o texto pra 6px no celular, abaixo do piso
           de fonte que as engines móveis aplicam; elas inflavam o texto por
           conta própria e o layout saía diferente do desenho. Pastilha e botão
           são cópia das medidas de RewardCardActions/RewardRedeemButton. -->
      <div
        v-if="isMobile"
        class="relative flex items-center gap-[10px]"
      >
        <div
          class="flex h-[36px] shrink-0 flex-col items-center justify-center whitespace-nowrap rounded-[8px] bg-white/5 px-[10px] py-[4px] text-center"
        >
          <span class="-mb-[2px] text-[9px] leading-none text-white/70">
            Expira em:
          </span>

          <WidgetsCountdown
            v-if="countDownTotalTime && !isCountdownFinished"
            :total-time="countDownTotalTime"
            @timer-finished="isCountdownFinished = true"
          >
            <template #process="{ time }">
              <span class="text-[11px] leading-none tabular-nums text-white">
                {{ `${time.hours}h:${time.minutes}m:${time.seconds}s` }}
              </span>
            </template>
            <template #finish>
              <span class="text-[11px] leading-none text-white">
                {{ $t('rewards.expired') }}
              </span>
            </template>
          </WidgetsCountdown>

          <span
            v-else
            class="text-[11px] leading-none text-white"
          >
            {{ $t('rewards.expired') }}
          </span>
        </div>

        <button
          class="border-solid flex h-[36px] min-w-0 flex-1 items-center justify-center gap-[6px] rounded-[5px] border-[#fc9984] bg-gradient-to-br from-[#ff704a] to-[#ff3400] px-[8px] py-[4px]"
          type="button"
          @click="acionar()"
        >
          <img
            v-if="isDownload"
            alt=""
            aria-hidden="true"
            class="size-[14px] shrink-0"
            :src="
              getAssetsUrlForCDN(
                '/assets/images/user-v2/rewards/app-only-download.svg',
              )
            "
          />

          <span class="truncate text-[10px] font-extrabold text-white">
            {{ isDownload ? 'Baixar o app e resgatar' : 'Resgatar prêmio' }}
          </span>
        </button>
      </div>

      <!-- mesma borda e mesmo gradiente do botão "RESGATAR PRÊMIO"
           (RewardRedeemButton.vue) — o Figma reusa aquele estilo. -->
      <button
        v-else
        class="border-solid relative flex h-[calc(var(--u)*26)] w-full items-center justify-center gap-[calc(var(--u)*5.396)] rounded-[calc(var(--u)*4.625)] border-[#fc9984] bg-gradient-to-br from-[#ff704a] to-[#ff3400] px-[calc(var(--u)*7.709)]"
        type="button"
        @click="acionar()"
      >
        <img
          v-if="isDownload"
          alt=""
          aria-hidden="true"
          class="size-[calc(var(--u)*19)] shrink-0"
          :src="
            getAssetsUrlForCDN(
              '/assets/images/user-v2/rewards/app-only-download.svg',
            )
          "
        />

        <span
          class="text-[calc(var(--u)*9.251)] font-extrabold leading-[calc(var(--u)*9.251)] text-white"
        >
          {{ isDownload ? 'Baixar o app e resgatar' : 'Resgatar prêmio' }}
        </span>
      </button>

      <!-- A linha fica nas DUAS variantes: tirar ela mudava a estrutura e a
           altura do card, e a versão de dentro do app tem que ser igual à de
           fora. Muda só o texto. -->
      <p
        class="relative text-center font-extrabold text-[#fff261] [text-shadow:0_1px_2px_rgba(0,0,0,0.85)]"
        :class="
          isMobile
            ? 'text-[9px] leading-[13px]'
            : 'text-[calc(var(--u)*7)] leading-[calc(var(--u)*13)]'
        "
      >
        {{
          isDownload
            ? 'Válido somente para quem tem o app da ApostaTudo instalado.'
            : 'Prêmio exclusivo para quem tem o app da ApostaTudo.'
        }}
      </p>
    </div>
  </div>
</template>
